/**
 * Readable inks derived from the clinic primary colour, for presentation E
 * (ADR 0068, dark mode option A).
 *
 * The clinic colour stays the FILL of buttons, checked boxes and the row
 * selection stripe. Text and thin strokes drawn in that colour use
 * `--primary-text` instead, which this module derives so that it reaches
 * WCAG AA (4.5:1) on every surface it can sit on:
 *
 * - light: the clinic colour itself when it already passes, otherwise the
 *   smallest shade of it (mixed towards black) that does;
 * - dark: the smallest tint of it (mixed towards white) that does, so a navy
 *   or teal clinic stays recognisable instead of vanishing on the dark
 *   surfaces.
 *
 * Row hover and row selection backgrounds are tinted with the same colour
 * (light: the clinic colour; dark: its readable tint), and the selection tint
 * is lowered until body text, muted text and the ink still read at 4.5:1.
 *
 * The surfaces below mirror the E tokens of `index.css`; a test checks they
 * stay in step, and that the CSS fallbacks equal the values computed for the
 * teal fallback primary.
 */

export type Rgb = readonly [number, number, number];

export interface TemplateEInks {
  /** Ink for text and strokes (`--primary-text`), as "r g b" channels. */
  text: string;
  /** Row hover background (`--row-hover`), as "r g b" channels. */
  rowHover: string;
  /** Checked row background (`--row-sel`), as "r g b" channels. */
  rowSel: string;
}

export const AA = 4.5;

/** Presentation E surfaces (index.css, `:root[data-ui="e"]`). */
export const E_LIGHT = {
  background: [248, 249, 251],
  surface: [255, 255, 255],
  rowAlt: [245, 247, 249],
  foreground: [39, 39, 39],
  muted: [91, 102, 112],
} as const satisfies Record<string, Rgb>;

/** Presentation E dark surfaces (index.css, `:root[data-ui="e"].dark`). */
export const E_DARK = {
  background: [10, 22, 29],
  surface: [15, 32, 41],
  rowAlt: [18, 38, 49],
  foreground: [231, 238, 242],
  muted: [151, 170, 181],
} as const satisfies Record<string, Rgb>;

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

/** Share of the tint in the hover and selection backgrounds. */
const HOVER_SHARE = { light: 0.08, dark: 0.1 };
const SEL_SHARE = { max: { light: 0.16, dark: 0.22 }, min: 0.1 };

export function parseHex(hex: string): Rgb | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m?.[1]) return null;
  const int = Number.parseInt(m[1], 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2.x contrast ratio between two opaque colours. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** `a` mixed with `b`, `t` being the share of `b` (0 → a, 1 → b). */
export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [
    Math.round(a[0] * (1 - t) + b[0] * t),
    Math.round(a[1] * (1 - t) + b[1] * t),
    Math.round(a[2] * (1 - t) + b[2] * t),
  ];
}

export function toChannels(c: Rgb): string {
  return `${c[0]} ${c[1]} ${c[2]}`;
}

function minContrast(ink: Rgb, backgrounds: readonly Rgb[]): number {
  return Math.min(...backgrounds.map((bg) => contrastRatio(ink, bg)));
}

function derive(primary: Rgb, mode: "light" | "dark"): TemplateEInks {
  const s = mode === "light" ? E_LIGHT : E_DARK;
  const toward = mode === "light" ? BLACK : WHITE;
  // Steps of 1 % between the two shares, from the most marked selection down.
  const maxShare = SEL_SHARE.max[mode];
  for (let k = Math.round(maxShare * 100); k >= SEL_SHARE.min * 100; k -= 1) {
    const selShare = k / 100;
    for (let step = 0; step <= 100; step += 1) {
      const ink = mix(primary, toward, step / 100);
      // Light: backgrounds tinted by the clinic colour itself. Dark: by the
      // readable tint (a navy tint would not show on navy surfaces).
      const tint = mode === "light" ? primary : ink;
      const rowHover = mix(s.surface, tint, HOVER_SHARE[mode]);
      const rowSel = mix(s.surface, tint, selShare);
      const backgrounds: Rgb[] = [
        s.background,
        s.surface,
        s.rowAlt,
        rowHover,
        rowSel,
        // `bg-primary/5` and `bg-primary/10` (table heads, active menu entry).
        mix(s.surface, primary, 0.05),
        mix(s.surface, primary, 0.1),
      ];
      if (minContrast(ink, backgrounds) < AA) continue;
      const textOk =
        contrastRatio(s.foreground, rowSel) >= AA &&
        contrastRatio(s.muted, rowSel) >= AA;
      if (!textOk) break; // a lighter selection tint is needed
      return {
        text: toChannels(ink),
        rowHover: toChannels(rowHover),
        rowSel: toChannels(rowSel),
      };
    }
  }
  // Unreachable with the E surfaces (black / white always pass); kept as a
  // safe neutral answer for any future surface change.
  const ink = s.foreground;
  return {
    text: toChannels(ink),
    rowHover: toChannels(mix(s.surface, ink, HOVER_SHARE[mode])),
    rowSel: toChannels(mix(s.surface, ink, SEL_SHARE.min)),
  };
}

/** Light and dark inks for a clinic colour (hex). Null if the hex is invalid. */
export function templateEInks(
  hex: string,
): { light: TemplateEInks; dark: TemplateEInks } | null {
  const primary = parseHex(hex);
  if (!primary) return null;
  return { light: derive(primary, "light"), dark: derive(primary, "dark") };
}

/**
 * Inline variables read by the E rules of `index.css` only. Without the
 * module, no rule reads them: the rendering is unchanged.
 */
export function applyTemplateEInks(
  hex: string,
  root: HTMLElement = document.documentElement,
): void {
  const inks = templateEInks(hex);
  if (!inks) return;
  root.style.setProperty("--e-ink-light", inks.light.text);
  root.style.setProperty("--e-row-hover-light", inks.light.rowHover);
  root.style.setProperty("--e-row-sel-light", inks.light.rowSel);
  root.style.setProperty("--e-ink-dark", inks.dark.text);
  root.style.setProperty("--e-row-hover-dark", inks.dark.rowHover);
  root.style.setProperty("--e-row-sel-dark", inks.dark.rowSel);
}
